"use client";

import { useTranslations } from "@frontend/lib/next-intl-typed";
import { Button } from "@frontend/shared/components/ui/button";
import { cn } from "@frontend/shared/lib/utils";
import { Loader2, MoveRight, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import { useDebounceValue } from "usehooks-ts";
import { formatRequiredLabel } from "./IntakeFormQuestion";

type SearchResult = {
  ehrId: string;
  value: string;
};

export type IntakeListEntry = {
  ehrId: string | undefined;
  value: string;
};

const searchListOptions = async (
  endpoint: string,
  searchTerm: string,
  limit = 10,
) => {
  const response = await fetch(
    `${endpoint}?term=${encodeURIComponent(searchTerm)}&limit=${limit}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        accept: "*/*",
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to search: ${response.statusText}`);
  }

  const data = await response.json();
  if (!Array.isArray(data)) {
    throw new Error("Response data was not an array");
  }

  return data;
};

export const IntakeListInput = ({
  field,
  setValue,
  error,
  questionText,
  questionAttributes,
  isRequired,
  clearErrors,
  hideQuestionText,
  isAdminView,
}) => {
  const formattedLabel = formatRequiredLabel(
    questionText,
    isRequired,
    hideQuestionText,
  );
  // Local state for input and list items
  const [inputValue, setInputValue] = useState("");
  const t = useTranslations("TypeformIntake");

  const parsedFromValue = useMemo(() => {
    if (field.value && typeof field.value === "string") {
      try {
        const arrayValue = JSON.parse(field.value) as IntakeListEntry[];
        return arrayValue;
      } catch (error) {
        console.warn(
          "Error parsing list input value for IntakeListInput",
          error,
        );
        return [];
      }
    }
    if (Array.isArray(field.value)) {
      return field.value;
    }
    return [];
  }, [field.value]);

  const [items, setItems] = useState<IntakeListEntry[]>(parsedFromValue);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [debouncedSearchValue] = useDebounceValue(inputValue, 300);

  const hasDropdownValues =
    questionAttributes?.listOptionsEndpoint !== undefined;

  // Search handler
  const searchItems = useCallback(
    async (searchTerm: string) => {
      if (!questionAttributes?.listOptionsEndpoint || !searchTerm.trim()) {
        setSearchResults([]);
        return;
      }

      try {
        setIsSearching(true);
        const data = await searchListOptions(
          questionAttributes.listOptionsEndpoint,
          searchTerm,
        );
        setSearchResults(data);
      } catch (error) {
        console.error("Error fetching search results:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [questionAttributes?.listOptionsEndpoint],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: don't use searchItems in the dependency array
  useEffect(() => {
    searchItems(debouncedSearchValue);
  }, [debouncedSearchValue]);

  // Add manual item handler
  const handleAddManualItem = (e?: React.FormEvent | React.MouseEvent) => {
    if (e) e.preventDefault();
    const trimmed = inputValue.trim();
    if (trimmed.length === 0) return;

    // auto-select an item from dropdown if search term is exact match
    if (searchResults.length > 0) {
      const result = searchResults.find(
        (result) => result.value.toLowerCase() === trimmed.toLowerCase(),
      );
      if (result) {
        handleAddEhrItem(result);
        return;
      }
    }

    setItems((prev) => [...prev, { ehrId: undefined, value: trimmed }]);
    setInputValue("");
    setShowDropdown(false);
  };

  // Add EHR item handler
  const handleAddEhrItem = (result: SearchResult) => {
    setItems((prev) => [...prev, { ehrId: result.ehrId, value: result.value }]);
    setInputValue("");
    setShowDropdown(false);
  };

  // Remove item handler
  const handleRemoveItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    setValue(field.name, items);
  }, [items, items.length, setValue, field.name]);

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim().length > 0) {
      e.preventDefault();
      handleAddManualItem();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const inputIsEmpty = inputValue.trim().length === 0;

  if (isAdminView) {
    let stringValue = "";
    if (field.value) {
      try {
        const arrayValue = JSON.parse(
          field.value as string,
        ) as IntakeListEntry[];
        stringValue = arrayValue.map((item) => item.value).join(", ");
      } catch (error) {
        console.warn(
          "Error parsing list input value for admin view for IntakeListInput",
          error,
        );
        stringValue = "N/A";
      }
    } else {
      stringValue = "N/A";
    }
    return (
      <div className="w-full flex flex-col gap-1 relative">
        <p className="text-sm text-black">
          <label htmlFor={field.name}>{formattedLabel}</label>
        </p>
        <input
          type="text"
          value={stringValue}
          disabled
          style={{
            height: "100%",
            backgroundColor: "lightgray",
          }}
          className={cn(
            "w-full border rounded bg-white/10 p-2.5 text-sm font-normal transition-all duration-200 focus:outline-none placeholder:text-gray-400 disabled:bg-gray-200 disabled:cursor-not-allowed",
          )}
        />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-1 relative">
      {/* Input Row */}
      <p className="text-sm text-black">
        <label htmlFor={field.name}>{formattedLabel}</label>
      </p>
      <form
        className="flex w-full gap-3 items-center"
        onSubmit={handleAddManualItem}
        autoComplete="off"
      >
        <div className="flex-grow relative" ref={dropdownRef}>
          <input
            type="text"
            className={cn(
              "w-full rounded-full border border-black bg-white p-4 text-base font-medium placeholder:text-gray-500 focus:outline-none focus:ring-0 transition-all duration-300 placeholder:text-pcv2-grey-text placeholder:font-light",
              inputIsEmpty && "bg-gray-100 border-none",
            )}
            placeholder={hasDropdownValues ? t("StartTypingToSearch") : ""}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowDropdown(true);
            }}
            onKeyDown={handleKeyDown}
            aria-label={questionText}
          />

          {/* Dropdown */}
          {showDropdown &&
            hasDropdownValues &&
            (searchResults.length > 0 || isSearching) && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {isSearching ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="animate-spin" size={20} />
                  </div>
                ) : (
                  searchResults.map((result, index) => (
                    <button
                      key={`${result.ehrId}-${index}`}
                      type="button"
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                      onClick={() => handleAddEhrItem(result)}
                    >
                      {result.value}
                    </button>
                  ))
                )}
              </div>
            )}
        </div>
        <button
          type="submit"
          className="min-w-[100px] flex items-center justify-center rounded-full border border-pcv2-grey-stroke bg-white px-6 py-4 font-semibold text-black transition-all duration-200 hover:bg-gray-100 disabled:opacity-50"
          disabled={inputIsEmpty}
          tabIndex={0}
        >
          Enter <MoveRight className="ml-2" size={20} />
        </button>
      </form>

      {/* List Row */}
      <div className="flex flex-wrap gap-3 mt-2">
        {items.map((item, idx) => (
          <Button
            key={`${item.value}-${item.ehrId}`}
            className="flex items-center text-black rounded-full border border-black px-5 py-2 bg-white text-base font-medium cursor-pointer transition-all duration-150 hover:bg-gray-50"
            onClick={() => handleRemoveItem(idx)}
            tabIndex={0}
            type="button"
            aria-label={`Remove ${item.value}`}
          >
            <X className="mr-2" size={18} />
            {item.value}
          </Button>
        ))}
      </div>
    </div>
  );
};
